import React, { useEffect, useState } from "react";
import PhoneInput from "../PhoneInput";
import { normalizeToE164 } from "../../utils/phoneUtils";
import { sendSMSOTP, verifySMSOTP } from "../../utils/smsOtpService";
import "./phoneNumberModal.scss";

const OTP_LENGTH = 6;
const OTP_VALIDITY_SECONDS = 120;

const PhoneNumberModal = ({
  open,
  onClose,
  onSave,
  userEmail = "",
  title = "Mobile Number Required",
  message = "Please enter your mobile number for Multi-Factor Authentication (MFA)",
  labelname = "Mobile Number",
  allowDismiss = true,
  saveLabel = "Save",
}) => {
  const [step, setStep] = useState("phone");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [pendingPhone, setPendingPhone] = useState("");
  const [phoneError, setPhoneError] = useState(null);
  const [otpError, setOtpError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState(() => new Array(OTP_LENGTH).fill(""));
  const [resendTimer, setResendTimer] = useState(OTP_VALIDITY_SECONDS);

  const resetState = () => {
    setStep("phone");
    setPhoneNumber("");
    setPendingPhone("");
    setPhoneError(null);
    setOtpError(null);
    setOtp(new Array(OTP_LENGTH).fill(""));
    setResendTimer(OTP_VALIDITY_SECONDS);
  };

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open]);

  useEffect(() => {
    if (step !== "otp" || resendTimer === 0) return undefined;
    const interval = setInterval(() => {
      setResendTimer((value) => value - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [step, resendTimer]);

  const handlePhoneChange = (value) => {
    setPhoneNumber(value || "");
    setPhoneError(null);
  };

  const validatePhone = () => {
    if (!phoneNumber || phoneNumber.trim() === "") {
      setPhoneError("Please enter your mobile number");
      return null;
    }

    const normalizedPhone = normalizeToE164(phoneNumber);
    if (!normalizedPhone) {
      setPhoneError("Please enter a valid mobile number");
      return null;
    }

    if (!normalizedPhone.startsWith("+")) {
      setPhoneError("Phone number must include country code");
      return null;
    }

    return normalizedPhone;
  };

  const sendOtpToPhone = async (normalizedPhone) => {
    const result = await sendSMSOTP(
      normalizedPhone,
      userEmail || null,
      { requireSms: true },
    );
    if (!result.success) {
      throw new Error(result.message || "Failed to send OTP");
    }
    setPendingPhone(normalizedPhone);
    setStep("otp");
    setOtp(new Array(OTP_LENGTH).fill(""));
    setOtpError(null);
    setResendTimer(OTP_VALIDITY_SECONDS);
  };

  const handleSendOtp = async () => {
    const normalizedPhone = validatePhone();
    if (!normalizedPhone) return;

    setLoading(true);
    try {
      await sendOtpToPhone(normalizedPhone);
    } catch (error) {
      setPhoneError(
        error?.message || "Failed to send OTP. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!pendingPhone || resendTimer > 0) return;
    setLoading(true);
    setOtpError(null);
    try {
      await sendOtpToPhone(pendingPhone);
    } catch (error) {
      setOtpError(error?.message || "Failed to resend OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (event, index) => {
    const value = event.target.value;
    if (value && Number.isNaN(Number(value))) return;
    const next = [...otp];
    next[index] = value.slice(-1);
    setOtp(next);
    setOtpError(null);
    if (value && index < OTP_LENGTH - 1) {
      document.getElementById(`phone-modal-otp-${index + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (event, index) => {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`phone-modal-otp-${index - 1}`)?.focus();
    }
  };

  const handleOtpPaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = new Array(OTP_LENGTH).fill("");
    pasted.split("").forEach((digit, index) => {
      next[index] = digit;
    });
    setOtp(next);
    setOtpError(null);
    document
      .getElementById(`phone-modal-otp-${Math.min(pasted.length, OTP_LENGTH) - 1}`)
      ?.focus();
  };

  const handleVerifyAndSave = async () => {
    const code = otp.join("");
    if (code.length !== OTP_LENGTH) {
      setOtpError("Please enter the 6-digit OTP");
      return;
    }

    setLoading(true);
    try {
      const verifyResult = await verifySMSOTP(
        code,
        pendingPhone,
        userEmail || null,
      );
      if (!verifyResult.success) {
        setOtpError(verifyResult.message || "Invalid OTP. Please try again.");
        return;
      }

      await onSave(pendingPhone);
      resetState();
      onClose?.();
    } catch (error) {
      setOtpError(
        error?.message || "Failed to verify OTP. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!allowDismiss) return;
    resetState();
    onClose?.();
  };

  const handleBackToPhone = () => {
    setStep("phone");
    setOtp(new Array(OTP_LENGTH).fill(""));
    setOtpError(null);
    setResendTimer(OTP_VALIDITY_SECONDS);
  };

  if (!open) return null;

  return (
    <div
      className="phone-number-modal-overlay"
      onClick={allowDismiss ? handleClose : undefined}
    >
      <div className="phone-number-modal" onClick={(e) => e.stopPropagation()}>
        {step === "phone" ? (
          <>
            <h2 className="phone-number-modal-title">{title}</h2>
            <p className="phone-number-modal-question">{message}</p>

            <div className="phone-number-modal-input">
              <PhoneInput
                labelname={labelname}
                value={phoneNumber}
                onChange={handlePhoneChange}
                error={phoneError !== null}
                errmsg={phoneError}
                required
                placeholder="Enter your mobile number"
              />
            </div>

            <div className="phone-number-modal-buttons">
              {allowDismiss && (
                <button
                  type="button"
                  className="phone-number-modal-btn phone-number-modal-btn-cancel"
                  onClick={handleClose}
                  disabled={loading}
                >
                  Cancel
                </button>
              )}
              <button
                type="button"
                className="phone-number-modal-btn phone-number-modal-btn-save"
                onClick={handleSendOtp}
                disabled={loading}
              >
                {loading ? "Sending OTP..." : "Send OTP"}
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="phone-number-modal-title">Verify Phone Number</h2>
            <p className="phone-number-modal-question">
              Enter the OTP sent to your mobile number.
            </p>

            <div className="phone-number-modal-verified-phone">
              <span className="phone-number-modal-verified-label">
                Phone number
              </span>
              <span className="phone-number-modal-verified-value">
                {pendingPhone}
              </span>
            </div>

            <p className="phone-number-modal-otp-note">
              If you didn&apos;t receive the OTP, please check the country code
              and the number once.
            </p>

            <div className="phone-number-modal-otp-container" onPaste={handleOtpPaste}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  id={`phone-modal-otp-${index}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(event) => handleOtpChange(event, index)}
                  onKeyDown={(event) => handleOtpKeyDown(event, index)}
                  className="phone-number-modal-otp-input"
                  disabled={loading}
                />
              ))}
            </div>

            {otpError ? (
              <p className="phone-number-modal-otp-error">{otpError}</p>
            ) : null}

            <div className="phone-number-modal-resend">
              {resendTimer > 0 ? (
                <span>
                  Resend OTP in {Math.floor(resendTimer / 60)}:
                  {String(resendTimer % 60).padStart(2, "0")}
                </span>
              ) : (
                <button
                  type="button"
                  className="phone-number-modal-resend-btn"
                  onClick={handleResendOtp}
                  disabled={loading}
                >
                  Resend OTP
                </button>
              )}
            </div>

            <button
              type="button"
              className="phone-number-modal-back-link"
              onClick={handleBackToPhone}
              disabled={loading}
            >
              Change phone number
            </button>

            <div className="phone-number-modal-buttons">
              {allowDismiss && (
                <button
                  type="button"
                  className="phone-number-modal-btn phone-number-modal-btn-cancel"
                  onClick={handleClose}
                  disabled={loading}
                >
                  Cancel
                </button>
              )}
              <button
                type="button"
                className="phone-number-modal-btn phone-number-modal-btn-save"
                onClick={handleVerifyAndSave}
                disabled={loading}
              >
                {loading ? "Verifying..." : saveLabel}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PhoneNumberModal;
